/**
 * Договор CRM из prokat — `/api/contracts/…` (спека `docs/specs/2026-10-22-contract-crud-design.md`).
 * Версия для записи берётся ТОЛЬКО из `GET /api/contracts/{id}` (`version`), после правки — из
 * `result.contract.version`. Правка пишет в боевую CRM (право `contract_edit`).
 */
import { type QueryClient, useQuery, useQueryClient } from "@tanstack/react-query";
import { useCallback } from "react";

import { CONTRACT_EDIT, useCan } from "@/lib/access";
import { ApiFailure, apiFetch, query } from "@/lib/api";

/**
 * Строка договора — СЫРЫЕ значения базы (`read.прочитать` текстом переводит только `dogovor`):
 * числа — числами, пусто — `null` (проверено на живом ответе crm-dev, договор 91253).
 */
export type RawContractRow = {
  id: number;
  dogovorID: number;
  productName: string | null;
  article: number | null;
  serial: number | null;
  priceAccount: number | null;
  priceDay: number | null;
  /** 0/1 */
  isSale: number;
  productComment: string | null;
  /** 0/1 */
  podwiv: number;
  dogovorProductID: number | null;
  dealProductID: number | null;
  podzakazID: number | null;
  /** 0/1 */
  isDeleted: number;
  userID: number | null;
};

type PassportDoc = { id: number; type: number; at: string };

/** Ответ `GET /api/contracts/{id}` (= `read.прочитать`). */
export type RawContract = {
  dogovor: { id: number; [field: string]: string | number | null };
  products: RawContractRow[];
  deleted: RawContractRow[];
  passport: { number: string; page1: boolean; page2: boolean; docs: PassportDoc[] };
  version: string;
  /** Рекомендованные цены живых строк по `id` строки (прайс серии `sPrice`); нет прайса — строки нет. */
  prices?: Record<string, RowPrices>;
};

/** Прайс серии (`serial.card.цена`): статус 1 — цены, 2 — запрет, 3 — уточнить у владельца. */
export type RowPrices = {
  rent_status: number | null;
  /** Прокат за 1, 2, 3 дня. */
  rent: [number | null, number | null, number | null];
  rent_min: number | null;
  sale_status: number | null;
  sale: number | null;
  sale_min: number | null;
};

type Option = { id: number; name: string; short?: string };
/** `GET /api/contracts/options`: справочники формы; `write` — правка из prokat включена (иначе кнопок нет). */
type ContractOptions = { messengers: Option[]; categories: Option[]; locations: Option[]; write?: boolean };

/** Пределы полей CRM: цена — `INT UNSIGNED`, дни — `TINYINT UNSIGNED`. */
export const PRICE_MAX = 4294967295;
export const DAYS_MAX = 255;

export const digits = (s: string) => s.replace(/\D/g, "");
/** Номер как хранит CRM (11 цифр, 8… → 7…) — сервер нормализует так же. */
export const phoneKey = (s: string) => {
  const t = digits(s);
  return t.length === 11 && t[0] === "8" ? `7${t.slice(1)}` : t;
};

export type ContractChange = {
  id: number;
  op: string;
  user_id: number | null;
  user_name: string;
  at: string | null;
  before: unknown;
  after: unknown;
  sync: string | null;
  sync_detail: string | null;
  error: string | null;
  /** Причина, которую ввёл сотрудник (удаление товара — обязательна). */
  comment?: string | null;
  rollback_of: number | null;
  rolled_back_by: number | null;
};

export type ContractWriteResult = {
  change_id: number;
  dogovor_id: number;
  sync: { status: string; deal_id: number | null; detail: string | null };
  warnings: string[];
  contract?: RawContract;
};

/** Услуга справочника CRM (строка договора без серии). */
type ContractService = { id: number; serviceName: string; article: string | number | null; serial: number | null };

export const contractQuery = (id: number) => ({
  queryKey: ["contract", id] as const,
  queryFn: () => apiFetch<RawContract>(`/api/contracts/${id}`),
  staleTime: 0,
});

export const contractOptionsQuery = () => ({
  queryKey: ["contract-options"] as const,
  queryFn: () => apiFetch<ContractOptions>("/api/contracts/options"),
  staleTime: 10 * 60_000,
});

export const contractChangesQuery = (id: number) => ({
  queryKey: ["contract-changes", id] as const,
  queryFn: () => apiFetch<ContractChange[]>(`/api/contracts/${id}/changes`),
});

export const contractServicesQuery = () => ({
  queryKey: ["contract-services"] as const,
  queryFn: () => apiFetch<ContractService[]>("/api/contracts/services"),
  staleTime: 10 * 60_000,
});

const send = <T>(method: string, path: string, body?: unknown) =>
  apiFetch<T>(path, { method, ...(body === undefined ? {} : { body: JSON.stringify(body) }) });

type W = ContractWriteResult;
/** Ручки DELETE, sync и rollback читают `version` и `chat_id` из адреса, а не из тела. */
const qs = (p: Record<string, string | number | null | undefined>) => query(p);
const chat = (chatId: number | null | undefined) => chatId || null;

export const createContract = (dealId: number, f: { phone: string; name: string }, chatId: number | null) =>
  send<W>("POST", `/api/deals/${dealId}/contract`, { ...f, chat_id: chat(chatId) });

export const patchContract = (id: number, fields: Record<string, unknown>, version: string, chatId: number | null) =>
  send<W>("PATCH", `/api/contracts/${id}`, { fields, version, chat_id: chat(chatId) });

const addContractProduct = (
  id: number,
  p: { serial?: number; service?: number; price?: number; price_day?: number; is_sale?: boolean },
  version: string,
  chatId: number | null,
) => send<W>("POST", `/api/contracts/${id}/products`, { ...p, version, chat_id: chat(chatId) });

export const patchContractRow = (
  id: number,
  rowId: number,
  fields: Record<string, unknown>,
  version: string,
  chatId: number | null,
) => send<W>("PATCH", `/api/contracts/${id}/products/${rowId}`, { fields, version, chat_id: chat(chatId) });

/** Причина удаления обязательна (3–300 знаков): журнал prokat и история CRM. */
export const DELETE_REASON_MIN = 3;
export const DELETE_REASON_MAX = 300;

export const deleteContractRow = (id: number, rowId: number, version: string, comment: string, chatId: number | null) =>
  send<W>("DELETE", `/api/contracts/${id}/products/${rowId}${qs({ version, comment, chat_id: chatId })}`);

export const restoreContractRow = (id: number, rowId: number, version: string, chatId: number | null) =>
  send<W>("POST", `/api/contracts/${id}/products/${rowId}/restore`, { version, chat_id: chat(chatId) });

export const syncContract = (id: number, chatId: number | null) =>
  send<W>("POST", `/api/contracts/${id}/sync${qs({ chat_id: chatId })}`);

export const rollbackChange = (id: number, changeId: number, chatId: number | null) =>
  send<W>("POST", `/api/contracts/${id}/changes/${changeId}/rollback${qs({ chat_id: chatId })}`);

/** Выбранное в договор (`ProductPicker`): товар серией или услуга справочника; пусто — CRM оставит поле пустым. */
export type PickedItem =
  | { kind: "serial"; serial: number; name: string; price: number | null; days: number | null }
  | { kind: "service"; service: number; name: string; price: number | null };

/** «название · серия» — так товар подписан везде; у услуги — только название. Артикул не показываем. */
export const pickedLabel = (i: PickedItem) => (i.kind === "serial" ? `${i.name} · ${i.serial}` : i.name);

type AddPickedResult = {
  /** Сколько добавлено (с начала списка). */
  done: number;
  /** Предупреждения ответов (пересечения с бронями) с подписью «название · серия». */
  warnings: string[];
  /** Первая ошибка — на ней остановились; `null` — добавлено всё. */
  error: unknown;
  /** Ошибка случилась при перечитывании договора после записи `done`-й строки, а не на самой записи. */
  reading: boolean;
  /** Версия договора после последней удачной записи; не узнали — `null`. */
  version: string | null;
};

/**
 * Добавить выбранное по очереди (**пишет в боевую CRM**): каждая запись — со свежей версией договора
 * (из `result.contract.version`, иначе перечитать `GET /api/contracts/{id}`); на первой ошибке — стоп.
 * Кэши не сбрасывает — это делает вызывающий (`refreshContract`) и после ошибки тоже.
 */
export async function addPickedItems(
  dogovorId: number,
  items: PickedItem[],
  version: string,
  chatId: number | null,
  qc: QueryClient,
  onStep?: (done: number, total: number) => void,
): Promise<AddPickedResult> {
  let ver = version;
  // Версия, которую знаем точно: после записи и до перечитывания её нет.
  let known: string | null = version;
  const warnings: string[] = [];
  let done = 0;
  let reading = false;
  try {
    for (const it of items) {
      onStep?.(done, items.length);
      reading = false;
      const r = await addContractProduct(
        dogovorId,
        {
          ...(it.kind === "serial" ? { serial: it.serial } : { service: it.service }),
          ...(it.price != null ? { price: it.price } : {}),
          ...(it.kind === "serial" && it.days != null ? { price_day: it.days } : {}),
        },
        ver,
        chatId,
      );
      done += 1;
      warnings.push(...r.warnings.map((w) => `${pickedLabel(it)}: ${w}`));
      reading = true;
      known = null;
      ver = r.contract?.version ?? (await qc.fetchQuery(contractQuery(dogovorId))).version;
      known = ver;
    }
  } catch (error) {
    return { done, warnings, error, reading, version: known };
  }
  return { done, warnings, error: null, reading: false, version: known };
}

/** После записи сбросить всё, что показывает договор (Global Constraints). */
export function refreshContract(qc: QueryClient, dealId: number, dogovorId: number | null, chatId: number | null) {
  void qc.invalidateQueries({ queryKey: ["deal-contract", dealId] });
  void qc.invalidateQueries({ queryKey: ["deal-card", dealId] });
  if (dogovorId !== null) {
    void qc.invalidateQueries({ queryKey: ["contract", dogovorId] });
    void qc.invalidateQueries({ queryKey: ["contract-changes", dogovorId] });
  }
  if (chatId) void qc.invalidateQueries({ queryKey: ["chat-actions", chatId] });
}

export function useContractRefresh(dealId: number, dogovorId: number | null, chatId: number | null) {
  const qc = useQueryClient();
  return useCallback(() => refreshContract(qc, dealId, dogovorId, chatId), [qc, dealId, dogovorId, chatId]);
}

const STALE = [
  "CONTRACT_CHANGED",
  "CONTRACT_BUSY",
  "CONTRACT_SYNC_RUNNING",
  "ROW_NOT_FOUND",
  "ROW_DELETED",
  "ROW_NOT_DELETED",
  "CONTRACT_EXISTS",
];

/** Договор изменился или занят: перечитать и показать причину, а не «ошибку». */
export const isStale = (e: unknown): boolean =>
  e instanceof ApiFailure && (e.status === 404 || e.status === 409) && STALE.includes(e.code);

/** Текст ошибки записи: договор изменился или занят — его уже перечитали, так и скажем. */
export function writeError(e: unknown): string {
  if (!(e instanceof Error)) return String(e);
  return isStale(e) ? `${e.message}. Договор перечитан — проверьте и повторите.` : e.message;
}

/** Исход записи не узнали (сеть, шлюз 502/504): CRM могла записать — только перечитать. */
export const isUnknownOutcome = (e: unknown): boolean => !(e instanceof ApiFailure) || e.status === 502 || e.status === 504;

/**
 * Кнопки правки договора: право `contract_edit` И запись включена на сервере (`options.write`).
 * Пока справочники не пришли или запись выключена — правки не показываем.
 */
export function useContractWritable(): boolean {
  const can = useCan(CONTRACT_EDIT);
  const o = useQuery({ ...contractOptionsQuery(), enabled: can });
  return can && o.data?.write === true;
}

/**
 * Фото паспорта → object URL (вызывающий делает `URL.revokeObjectURL`). Ручка пишет просмотр в журнал.
 * В кабинете это обычный запрос с cookie; в панели расширения `window.fetch` подменён (`ext/bridge.ts`)
 * и идёт через service worker, который отдаёт не-JSON байтами (base64) — код тот же.
 */
/** Ответ-файл не 200: ошибка из тела `{error}`, если оно JSON. */
async function fileFailure(r: Response): Promise<ApiFailure> {
  let items: { code: string; message: string }[] = [];
  try {
    items = ((await r.json()) as { error?: typeof items }).error ?? [];
  } catch {
    /* не JSON — отдадим общий текст */
  }
  return new ApiFailure(r.status, items.length ? items : [{ code: "HTTP_ERROR", message: `Сервис ответил ${r.status}` }]);
}

export async function passportPhotoUrl(id: number, docId: number): Promise<string> {
  const r = await fetch(`/api/contracts/${id}/passport/${docId}`, { credentials: "same-origin" });
  if (!r.ok) throw await fileFailure(r);
  return URL.createObjectURL(await r.blob());
}

/** Пункт меню «Документы» вкладки «Договор» (= `document/registry.список`). */
export type ContractDocument = { code: string; name: string; formats: DocFormat[] };
export type DocFormat = "docx" | "pdf";

/** Поля формы претензии на невозврат (= `document/claim.ПОЛЯ`); `amount` — рубли цифрами. */
export type ClaimFields = {
  fio: string;
  pass_series: string;
  pass_number: string;
  issued_date: string;
  issued_by: string;
  dep_code: string;
  address: string;
  phone: string;
  amount: string;
};

/** Ответ `GET /api/contracts/{id}/documents/claim_nonreturn` (спека `docs/specs/2026-10-23-claim-nonreturn-design.md`). */
export type ClaimForm = {
  dogovor_id: number;
  deal_id: number | null;
  company: { name: string; inn: string; stamp: boolean; sign: boolean };
  /** «№ <сделка> от <дата>» — как договор назван в претензии. */
  contract: string;
  fields: ClaimFields;
  warnings: string[];
};

const docUrl = (id: number, code: string) => `/api/contracts/${id}/documents/${code}`;

export const contractDocumentsQuery = (id: number) => ({
  queryKey: ["contract-documents", id] as const,
  queryFn: () => apiFetch<ContractDocument[]>(`/api/contracts/${id}/documents`),
  staleTime: Infinity,
});

export const claimFormQuery = (id: number) => ({
  queryKey: ["contract-document", id, "claim_nonreturn"] as const,
  queryFn: () => apiFetch<ClaimForm>(docUrl(id, "claim_nonreturn")),
});

/** Документ файлом (право `passport_view`): паспорт сохраняется по номеру; браузер скачивает файл. → номер. */
export async function downloadDocument(id: number, code: string, fields: ClaimFields, format: DocFormat, chatId: number | null): Promise<string> {
  const r = await fetch(`${docUrl(id, code)}?format=${format}`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    credentials: "same-origin",
    body: JSON.stringify({ fields, chat_id: chatId }),
  });
  if (!r.ok) throw await fileFailure(r);
  const number = decodeURIComponent(r.headers.get("X-Doc-Number") ?? "");
  const url = URL.createObjectURL(await r.blob());
  const a = document.createElement("a");
  a.href = url;
  a.download = `Претензия ${number.replaceAll("/", " ")}.${format}`;
  document.body.append(a);
  a.click();
  a.remove();
  window.setTimeout(() => URL.revokeObjectURL(url), 10_000);
  return number;
}

/** Для поля ответа «Чатов»: текст сообщения и файл документа (PDF). */
export async function documentForChat(id: number, code: string, fields: ClaimFields, chatId: number | null): Promise<{ number: string; message: string; file: File }> {
  const d = await apiFetch<{ number: string; name: string; mime: string; file: string; message: string }>(`${docUrl(id, code)}?format=pdf&chat=1`, {
    method: "POST",
    body: JSON.stringify({ fields, chat_id: chatId }),
  });
  const bytes = Uint8Array.from(atob(d.file), (ch) => ch.charCodeAt(0));
  return { number: d.number, message: d.message, file: new File([bytes], d.name, { type: d.mime }) };
}
